import type { Call, Device } from '@twilio/voice-sdk';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import AppContext from '@/contexts/appContext';
import SoftphoneCallRepository from '@/repositories/SoftphoneCallRepository';
import { SoftphoneAddParticipantPayload } from '@/types/softphone';

export type TwilioDeviceStatus = 'idle' | 'connecting' | 'ready' | 'error';
export type MicPermissionStatus = 'granted' | 'denied' | 'prompt';

const REFRESH_BUFFER_SECONDS = 300;
// How far ahead of expiry the SDK emits tokenWillExpire. Its default is 10 seconds,
// which leaves no room for the round trip to our token endpoint.
const TOKEN_REFRESH_MS = 120_000;
// How long after an add-participant request a disconnect can still be the browser leg
// being redirected into the conference room rather than the call actually ending.
const CONFERENCE_HANDOFF_GRACE_MS = 10_000;
const STORAGE_KEY_INPUT = 'softphone_audio_input_device_id';
const STORAGE_KEY_OUTPUT = 'softphone_audio_output_device_id';

// AccessTokenInvalid, AccessTokenExpired, AuthenticationFailed.
const TOKEN_ERROR_CODES = new Set([20101, 20104, 20151]);

// device.connect() rejects with no value at all when the signaling stream closes:
// Twilio's promisifyEvents calls a bare reject(). A token it no longer accepts is the
// common cause, and it's worth one more attempt with a new device. Anything that
// arrives as a real Error (no microphone, no WebRTC, a call already active) is a hard
// failure that a retry would only make slower.
const isRecoverableConnectFailure = (err: unknown) =>
  !(err instanceof Error) || TOKEN_ERROR_CODES.has((err as { code?: number }).code as number);

// Quality warnings the Voice SDK raises on the Call object, phrased for the care team
// rather than repeating Twilio's metric names. Several can fire at once on one bad
// network, so only one toast is shown per degraded spell — the one for the highest
// tier reached so far. A later warning only re-toasts if it's worse than what's
// already shown; it never downgrades back to a milder message mid-spell.
// https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#warning-event
const CALL_QUALITY_WARNINGS: Record<string, { message: string; tier: number }> = {
  'high-rtt': { message: 'High network latency — the caller may hear a delay.', tier: 1 },
  'low-mos': { message: 'Poor call quality detected on this connection.', tier: 1 },
  'high-jitter': {
    message: 'Unstable network — audio may sound choppy or crackly.',
    tier: 1
  },
  'high-packet-loss': { message: 'Network packet loss — audio may cut out.', tier: 2 },
  'high-packets-lost-fraction': {
    message: 'Severe network packet loss — the call may drop.',
    tier: 3
  },
  'low-bytes-received': {
    message: 'No audio is arriving — you may not be able to hear the caller.',
    tier: 3
  },
  'low-bytes-sent': {
    message: 'No audio is being sent — check that the right microphone is selected.',
    tier: 3
  },
  'ice-connectivity-lost': { message: 'Connection lost — reconnecting the call…', tier: 4 }
};

const useTwilioDevice = () => {
  const { addMessage } = useContext(AppContext);
  const repository = SoftphoneCallRepository();
  const repositoryRef = useRef(repository);
  repositoryRef.current = repository;
  const deviceRef = useRef<Device | null>(null);
  const [status, setStatus] = useState<TwilioDeviceStatus>('idle');
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const activeCallRef = useRef(activeCall);
  activeCallRef.current = activeCall;

  const [activeCallRecordId, setActiveCallRecordId] = useState<string | null>(null);
  const [availableInputDevices, setAvailableInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [availableOutputDevices, setAvailableOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [inputDeviceId, setInputDeviceId] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY_INPUT)
  );
  const [outputDeviceId, setOutputDeviceId] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY_OUTPUT)
  );
  const [isOutputSelectionSupported, setIsOutputSelectionSupported] = useState(false);
  const [micPermission, setMicPermission] = useState<MicPermissionStatus>('prompt');

  useEffect(() => {
    if (!navigator.permissions) return;
    let permissionStatus: globalThis.PermissionStatus | null = null;
    navigator.permissions
      .query({ name: 'microphone' as globalThis.PermissionName })
      .then((status) => {
        permissionStatus = status;
        setMicPermission(status.state as MicPermissionStatus);
        status.onchange = () => setMicPermission(status.state as MicPermissionStatus);
      })
      .catch(() => {
        // Permissions API unsupported or microphone query not recognised — leave as 'prompt'
      });
    return () => {
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, []);

  const tokenExpiresAtRef = useRef(0);

  const fetchToken = useCallback(async (): Promise<string> => {
    const data = await repositoryRef.current.fetchToken();
    tokenExpiresAtRef.current = Date.now() + data.ttl * 1000;
    return data.token;
  }, []);

  const enumerateDevices = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      setAvailableInputDevices(devices.filter((d) => d.kind === 'audioinput'));
      setAvailableOutputDevices(devices.filter((d) => d.kind === 'audiooutput'));
      setIsOutputSelectionSupported(
        typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype
      );
    } catch {
      // Permission denied or API unavailable — leave lists empty
    }
  }, []);

  const refreshDeviceLists = useCallback((device: Device) => {
    if (!device.audio) return;
    setAvailableInputDevices(Array.from(device.audio.availableInputDevices.values()));
    setAvailableOutputDevices(Array.from(device.audio.availableOutputDevices.values()));
    setIsOutputSelectionSupported(device.audio.isOutputSelectionSupported);
  }, []);

  const applyStoredDevicePreferences = useCallback(async (device: Device) => {
    if (!device.audio) return;

    const storedInput = localStorage.getItem(STORAGE_KEY_INPUT);
    if (storedInput && device.audio.availableInputDevices.has(storedInput)) {
      try {
        await device.audio.setInputDevice(storedInput);
      } catch {
        // Device may have been unplugged since last session; ignore
      }
    }

    const storedOutput = localStorage.getItem(STORAGE_KEY_OUTPUT);
    if (
      storedOutput &&
      device.audio.isOutputSelectionSupported &&
      device.audio.availableOutputDevices.has(storedOutput)
    ) {
      try {
        await device.audio.speakerDevices.set(storedOutput);
      } catch {
        // Same — ignore stale device ID
      }
    }
  }, []);

  const refreshToken = useCallback(
    async (device: Device) => device.updateToken(await fetchToken()),
    [fetchToken]
  );

  // Used where we hold a token and have to judge whether it's too old to rely on. The
  // SDK's tokenWillExpire is an authoritative statement rather than a judgement call,
  // so it refreshes directly instead of coming through here — otherwise the two would
  // be coupled by a fragile inequality between this buffer and TOKEN_REFRESH_MS.
  const ensureFreshToken = useCallback(
    async (device: Device) => {
      if (Date.now() < tokenExpiresAtRef.current - REFRESH_BUFFER_SECONDS * 1000) return;
      await refreshToken(device);
    },
    [refreshToken]
  );

  const discardDevice = useCallback((device: Device) => {
    tokenExpiresAtRef.current = 0;
    if (deviceRef.current === device) deviceRef.current = null;
    device.destroy();
  }, []);

  const handleDeviceError = useCallback((error?: { code?: number }) => {
    setStatus('error');
    // Only invalidate the token, deliberately leaving the device alone: this can fire
    // mid-call and destroy() would hang up. The next dial's freshness check replaces
    // the token before connecting, which is all the recovery this needs.
    if (error && TOKEN_ERROR_CODES.has(error.code as number)) tokenExpiresAtRef.current = 0;
  }, []);

  const getOrCreateDevice = useCallback(async (): Promise<Device> => {
    const cached = deviceRef.current;
    if (cached) {
      if (cached.state !== 'destroyed') {
        await ensureFreshToken(cached);
        return cached;
      }
      discardDevice(cached);
    }
    setStatus('connecting');
    try {
      const { Device: TwilioDevice } = await import('@twilio/voice-sdk');
      const token = await fetchToken();
      const device = new TwilioDevice(token, {
        logLevel: 'warn',
        tokenRefreshMs: TOKEN_REFRESH_MS
      });
      device.on('error', handleDeviceError);
      // The SDK arms this from the ttl the signaling server reports and re-arms it each
      // time updateToken publishes a new one, so it beats anything we could schedule
      // ourselves: no clock skew, and it keeps an in-progress call's token alive without
      // us polling. It's still only best effort — a suspended tab drops the timer
      // entirely, which is what the check at dial time is for.
      device.on('tokenWillExpire', () => {
        refreshToken(device).catch(() => {
          setStatus('error');
          // Only worth interrupting someone mid-call; otherwise the next dial retries
          // and reports for itself.
          if (activeCallRef.current) {
            addMessage('error', 'Could not refresh the phone connection — this call may drop.');
          }
        });
      });
      device.on('registered', () => setStatus('ready'));
      await device.register();

      refreshDeviceLists(device);
      await applyStoredDevicePreferences(device);

      device.audio?.on('deviceChange', () => refreshDeviceLists(device));

      deviceRef.current = device;
      return device;
    } catch (err) {
      setStatus('error');
      throw err;
    }
  }, [
    fetchToken,
    refreshToken,
    addMessage,
    refreshDeviceLists,
    applyStoredDevicePreferences,
    ensureFreshToken,
    discardDevice,
    handleDeviceError
  ]);

  const setInputDevice = useCallback(
    async (deviceId: string) => {
      localStorage.setItem(STORAGE_KEY_INPUT, deviceId);
      setInputDeviceId(deviceId);
      if (deviceRef.current?.audio) {
        try {
          await deviceRef.current.audio.setInputDevice(deviceId);
        } catch {
          addMessage('error', 'Failed to switch microphone. Please try again.');
        }
      }
    },
    [addMessage]
  );

  const setOutputDevice = useCallback(
    async (deviceId: string) => {
      localStorage.setItem(STORAGE_KEY_OUTPUT, deviceId);
      setOutputDeviceId(deviceId);
      if (deviceRef.current?.audio?.isOutputSelectionSupported) {
        try {
          await deviceRef.current.audio.speakerDevices.set(deviceId);
        } catch {
          addMessage('error', 'Failed to switch speaker. Please try again.');
        }
      }
    },
    [addMessage]
  );

  // Predicting expiry can't be perfect — clock skew, a revoked API key, or a token
  // Twilio rejects for its own reasons all land the same way. Rather than guard each
  // case, treat a refused signaling connection as worth exactly one more attempt with
  // a device built from scratch. That's what keeps expiry invisible to the user.
  const connectWithFreshToken = useCallback(
    async (params: { To: string; record_id: string }): Promise<Call> => {
      for (let attempt = 0; ; attempt += 1) {
        const device = await getOrCreateDevice();
        // Re-acquire the stored mic preference in case it was released after the
        // previous call ended.
        await applyStoredDevicePreferences(device);
        try {
          return await device.connect({ params });
        } catch (err) {
          if (attempt > 0 || !isRecoverableConnectFailure(err)) throw err;
          discardDevice(device);
        }
      }
    },
    [getOrCreateDevice, applyStoredDevicePreferences, discardDevice]
  );

  // Timestamp until which a disconnect is treated as the hand-off into the conference
  // room rather than the end of the call. The redirect takes a second or two, and the
  // window is deliberately short: once the room is live, a disconnect is a real one —
  // the staff member hanging up, or the conference ending after the last other party
  // left — and has to clear the overlay.
  const conferenceHandoffUntilRef = useRef(0);
  const isConferenceHandoff = useCallback(() => Date.now() < conferenceHandoffUntilRef.current, []);

  const startCall = useCallback(
    async (to: string, personId?: string): Promise<Call> => {
      const startedAt = new Date().toISOString();
      let callRecordId: string | null = null;
      let answeredAt: string | null = null;

      try {
        const record = await repositoryRef.current.createCall({
          to_number: to,
          direction: 'outbound',
          started_at: startedAt,
          person_id: personId
        });
        callRecordId = record.id;
      } catch {
        // Non-fatal: proceed with the call even if record creation fails
      }

      const patchRecord = (fields: Parameters<typeof repositoryRef.current.updateCall>[1]) => {
        if (!callRecordId) return;
        repositoryRef.current.updateCall(callRecordId, fields).catch(() => {});
      };

      try {
        const call = await connectWithFreshToken({ To: to, record_id: callRecordId ?? '' });
        setActiveCall(call);
        setActiveCallRecordId(callRecordId);

        const releaseAudioStream = () => {
          // Release the getUserMedia stream held by the SDK so the browser mic
          // indicator clears and the OS releases the device lock.
          deviceRef.current?.audio?.unsetInputDevice().catch(() => {});
        };

        call.on('accept', () => {
          answeredAt = new Date().toISOString();
          patchRecord({
            status: 'in-progress',
            answered_at: answeredAt,
            call_sid: call.parameters?.CallSid
          });
        });

        call.on('reconnecting', () => {
          addMessage('warning', 'Network connection lost — attempting to reconnect the call…');
        });

        call.on('reconnected', () => {
          addMessage('success', 'Call reconnected.');
        });

        // Several warnings often trip together on one bad network. Toasting each one
        // separately reads as a stack of near-identical messages, so only one toast is
        // shown per degraded spell — re-toasting only when a warning worse than
        // anything shown so far comes in, so the message tracks the worst thing
        // currently wrong rather than the first thing that happened to fire.
        const activeWarnings = new Set<string>();
        let highestTierShown = 0;

        call.on('warning', (name: string) => {
          const warning = CALL_QUALITY_WARNINGS[name];
          if (!warning) return;
          activeWarnings.add(name);
          if (warning.tier <= highestTierShown) return;
          highestTierShown = warning.tier;
          addMessage('warning', warning.message);
        });

        call.on('warning-cleared', (name: string) => {
          if (!activeWarnings.delete(name) || activeWarnings.size > 0) return;
          highestTierShown = 0;
          addMessage('success', 'Call quality is back to normal.');
        });

        call.on('disconnect', () => {
          // Suppress the SDK disconnect fired when the call leg is redirected into a
          // conference room — the WebRTC media stream is still alive on the server side.
          if (isConferenceHandoff()) return;
          releaseAudioStream();
          // Only patch ring_duration here; Twilio webhook owns ended_at / duration / final status
          if (!answeredAt) {
            const ringMs = Date.now() - new Date(startedAt).getTime();
            patchRecord({
              status: 'no-answer',
              ended_at: new Date().toISOString(),
              ring_duration_in_seconds: Math.round(ringMs / 1000)
            });
          }
          setActiveCall(null);
          setActiveCallRecordId(null);
        });

        call.on('cancel', () => {
          if (isConferenceHandoff()) return;
          releaseAudioStream();
          const ringMs = Date.now() - new Date(startedAt).getTime();
          patchRecord({
            status: 'no-answer',
            ended_at: new Date().toISOString(),
            ring_duration_in_seconds: Math.round(ringMs / 1000)
          });
          setActiveCall(null);
          setActiveCallRecordId(null);
        });

        return call;
      } catch (err) {
        console.error('[softphone] startCall failed:', err);
        setStatus('error');
        patchRecord({ status: 'failed', ended_at: new Date().toISOString() });
        // The rejection may carry no value at all, so callers can't assume they were
        // handed an Error.
        throw err instanceof Error
          ? err
          : new Error('Could not reach the phone service. Please try again.');
      }
    },
    [connectWithFreshToken, addMessage, isConferenceHandoff]
  );

  const endCall = useCallback(() => {
    conferenceHandoffUntilRef.current = 0;
    activeCall?.disconnect();
  }, [activeCall]);

  const addParticipant = useCallback(
    async (participant: SoftphoneAddParticipantPayload): Promise<void> => {
      if (!activeCallRecordId) throw new Error('No active call');
      conferenceHandoffUntilRef.current = Date.now() + CONFERENCE_HANDOFF_GRACE_MS;
      try {
        await repositoryRef.current.addParticipant(activeCallRecordId, participant);
      } catch (err) {
        conferenceHandoffUntilRef.current = 0;
        throw err;
      }
    },
    [activeCallRecordId]
  );

  useEffect(() => {
    if (!activeCallRecordId) return;
    const heartbeat = () => repositoryRef.current.sendHeartbeat(activeCallRecordId).catch(() => {});
    heartbeat();
    const id = setInterval(heartbeat, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [activeCallRecordId]);

  useEffect(() => {
    return () => {
      deviceRef.current?.destroy();
    };
  }, []);

  return {
    startCall,
    endCall,
    addParticipant,
    activeCall,
    status,
    micPermission,
    availableInputDevices,
    availableOutputDevices,
    inputDeviceId,
    outputDeviceId,
    isOutputSelectionSupported,
    setInputDevice,
    setOutputDevice,
    enumerateDevices
  };
};

export default useTwilioDevice;
